import { DotIcon, MegaphoneIcon } from "@primer/octicons-react";
import Prism from "prismjs";
import { useEffect } from "react";
import { Footnote, Header, Sidebar } from "../../components";
import introAudio from "../../assets/audio/intro-to-pwafire.wav";
import "./landing.css";

const Docs = () => {
  useEffect(() => {
    Prism.highlightAll();
    document.title = "PWA Fire - Build More Capable PWAs & Web Apps";
  }, []);
  return (
    <article>
      <Header />
      <section className="docs-flex">
        <Sidebar />
        <section className="docs-flex-right fade-in docs post">
          <div className="date">
            <span>Last updated -: April 15 2025</span>
          </div>
          <h3>Build More Capable PWAs & Web Apps</h3>
          <p style={{ fontSize: "1rem" }}>
            A modern, modular library for building Progressive Web Apps with
            ease. PWAFire provides a comprehensive set of APIs and utilities to
            enhance your web applications with PWA capabilities. Built on top of
            Project Fugu, PWAFire helps bridge the gap between web and native
            app capabilities.
          </p>
          <p
            className="quote"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.2rem"
            }}
          >
            <span className="tag-label">
              <MegaphoneIcon />
              <span>NEW</span>
            </span>
            <span style={{ marginLeft: "0.5rem" }}>
              <a href="/write-files">Write Files API</a> is now available, check
              it out!
            </span>
          </p>

          <div className="audio-section">
            <h3>
              <MegaphoneIcon size={24} />
              Quick Introduction
            </h3>
            <p>
              Take a moment to listen to this brief overview of PWAFire's
              capabilities and how it can transform your web applications.
            </p>
            <figure className="audio-intro">
              <audio controls src={introAudio}>
                Your browser does not support the audio element.
              </audio>
            </figure>
            <p className="duration">
              Duration: ~2 minutes | Topics covered: Core features,
              installation, and use cases
            </p>
          </div>

          <h3>Features</h3>
          <div className="features-section">
            <div className="feature-column">
              <div className="feature-item">
                <span className="icon">
                  <DotIcon />
                </span>
                <span className="text">Modern, tree-shakeable API design</span>
              </div>
              <div className="feature-item">
                <span className="icon">
                  <DotIcon />
                </span>
                <span className="text">Comprehensive PWA capabilities</span>
              </div>
              <div className="feature-item">
                <span className="icon">
                  <DotIcon />
                </span>
                <span className="text">Zero dependencies</span>
              </div>
              <div className="feature-item">
                <span className="icon">
                  <DotIcon />
                </span>
                <span className="text">TypeScript support</span>
              </div>
            </div>
            <div className="feature-column">
              <div className="feature-item">
                <span className="icon">
                  <DotIcon />
                </span>
                <span className="text">Universal browser support</span>
              </div>
              <div className="feature-item">
                <span className="icon">
                  <DotIcon />
                </span>
                <span className="text">
                  Multiple import options (ESM, CJS, CDN)
                </span>
              </div>
              <div className="feature-item">
                <span className="icon">
                  <DotIcon />
                </span>
                <span className="text">
                  Consistent camelCase naming convention
                </span>
              </div>
            </div>
            <div className="feature-column">
              <div className="feature-item">
                <span className="icon">
                  <DotIcon />
                </span>
                <span className="text">Built-in feature detection</span>
              </div>
              <div className="feature-item">
                <span className="icon">
                  <DotIcon />
                </span>
                <span className="text">Comprehensive documentation</span>
              </div>
              <div className="feature-item">
                <span className="icon">
                  <DotIcon />
                </span>
                <span className="text">Extensive test coverage</span>
              </div>
            </div>
          </div>

          <h3>Installation</h3>
          <p>
            <DotIcon /> Using npm
          </p>
          <pre id="copy-code">
            <code className="language-bash">{`npm install pwafire@latest`}</code>
          </pre>
          <p>
            <DotIcon /> Using yarn
          </p>
          <pre id="copy-code">
            <code className="language-bash">{`yarn add pwafire@latest`}</code>
          </pre>
          <p>
            <DotIcon /> Using pnpm
          </p>
          <pre id="copy-code">
            <code className="language-bash">{`pnpm add pwafire@latest`}</code>
          </pre>

          <h3>Import Patterns</h3>
          <p>
            PWAFire supports two import patterns for optimal flexibility and
            tree-shaking:
          </p>
          <p>
            <DotIcon /> Root Import (Recommended for multiple features)
          </p>
          <pre id="copy-code">
            <code className="language-javascript">{`import { copyText, webShare } from "pwafire";`}</code>
          </pre>
          <p>
            <DotIcon /> Scoped Import (Recommended for single features)
          </p>
          <pre id="copy-code">
            <code className="language-javascript">{`import { copyText } from "pwafire/clipboard";
import { webShare } from "pwafire/web-share";`}</code>
          </pre>

          <h3>Basic Usage</h3>
          <pre id="copy-code">
            <code className="language-javascript">{`// Using root import
import { copyText } from "pwafire";

// Using scoped import (recommended for single features)
import { copyText } from "pwafire/clipboard";

const { ok, message } = await copyText("Text to copy");`}</code>
          </pre>

          <h3>CDN Usage</h3>
          <pre id="copy-code">
            <code className="language-javascript">{`<script type="module">
  // Using root import
  import { copyText } from "https://unpkg.com/pwafire@latest/lib/index.mjs";

  // Using scoped import
  import { copyText } from "https://unpkg.com/pwafire@latest/lib/pwa/clipboard/index.mjs";

  const result = await copyText("Text to copy");
</script>`}</code>
          </pre>

          <h3>Error Handling</h3>
          <pre id="copy-code">
            <code className="language-javascript">{`import { copyText } from "pwafire/clipboard";

const handleCopy = async (text: string) => {
  try {
    const { ok, message } = await copyText(text);
  } catch (error) {
    // Handle error
  }
};

await handleCopy("Hello World");`}</code>
          </pre>

          <h3>Feature Detection</h3>
          <pre id="copy-code">
            <code className="language-javascript">{`// Using root import
import { check } from "pwafire";

// Using scoped import
import { check } from "pwafire/check";

const checkFeatures = async () => {
  const [isShareSupported, isClipboardSupported] = await Promise.all([
    check.webShare(),
    check.clipboard()
  ]);
};

await checkFeatures();`}</code>
          </pre>

          <h3>Browser Support</h3>
          <div className="browser-support">
            <div className="browser-item">
              <DotIcon />
              <span>Chrome 80+</span>
            </div>
            <div className="browser-item">
              <DotIcon />
              <span>Microsoft Edge</span>
            </div>
            <div className="browser-item">
              <DotIcon />
              <span>Firefox</span>
            </div>
            <div className="browser-item">
              <DotIcon />
              <span>Safari</span>
            </div>
          </div>

          <h3>Testing</h3>
          <p>
            <DotIcon /> Run tests
          </p>
          <pre id="copy-code">
            <code className="language-bash">{`npm test`}</code>
          </pre>
          <p>
            <DotIcon /> Run tests with coverage
          </p>
          <pre id="copy-code">
            <code className="language-bash">{`npm run test:coverage`}</code>
          </pre>

          <h3>Contributing</h3>
          <p>We welcome contributions! Here's how to get started:</p>
          <p>
            <DotIcon /> Clone the repository
          </p>
          <pre id="copy-code">
            <code className="language-bash">{`git clone https://github.com/pwafire/pwafire.git`}</code>
          </pre>
          <p>
            <DotIcon /> Install dependencies
          </p>
          <pre id="copy-code">
            <code className="language-bash">{`npm install`}</code>
          </pre>
          <p>
            <DotIcon /> Start development
          </p>
          <pre id="copy-code">
            <code className="language-bash">{`npm run dev`}</code>
          </pre>

          <h3>License</h3>
          <p>PWAFire is licensed under the MIT License.</p>
          <p className="quote">MIT © PWAFire</p>

          <h3>Community</h3>
          <p>Join our community and stay updated:</p>
          <p>
            <DotIcon /> Follow us on{" "}
            <a
              href="https://twitter.com/pwafire"
              target="_blank"
              rel="noreferrer"
            >
              Twitter
            </a>{" "}
            for updates and announcements
          </p>
          <p>
            <DotIcon /> Found a bug? Please create an{" "}
            <a
              href="https://github.com/pwafire/pwafire/issues"
              target="_blank"
              rel="noreferrer"
            >
              issue
            </a>{" "}
            to help us improve!
          </p>

          <section>
            <h3>Playground</h3>
            <p className="quote">
              Have a look at our demo app to see{" "}
              <a
                href="https://stackblitz.com/edit/pwafire?file=src%2Findex.ts"
                target="_blank"
                rel="noreferrer"
              >
                how it works
              </a>
              . To learn more about pwafire and for other developer resources,
              go to{" "}
              <a href="https://pwafire.org" target="_blank" rel="noreferrer">
                www.pwafire.org
              </a>
            </p>
          </section>
          <Footnote />
        </section>
      </section>
    </article>
  );
};

export default Docs;
